import React, { ReactElement } from "react";
import { Box, SvgIconProps, Theme } from "@mui/material";
import { Account, AccountTypeEnum } from "@rentablo/api/apiv1";
//#region Common icons
import AssetsIcon from "@icons/myicons/gold.svg?react";
import DepotIcon from "@icons/myicons/chart-square-1.svg?react";
import CashIcon from "@icons/myicons/cash.svg?react";
import InsuranceIcon from "@icons/myicons/insurance.svg?react";
import RefIcon from "@icons/myicons/arrow-down-right.svg?react";
import HomeIcon from "@icons/myicons/home-house.svg?react";
//#endregion
//#region Cashflow icons
import LifestyleIcon from "@icons/myicons/lifestyle.svg?react";
import ShoppingIcon from "@icons/myicons/shopping.svg?react";
import BankCreditIcon from "@icons/myicons/bank-and-credit.svg?react";
import HealthIcon from "@icons/myicons/health.svg?react";
import CustomIcon from "@icons/myicons/custom.svg?react";
import TravelIcon from "@icons/myicons/travel.svg?react";
//#endregion
//#region Contracts category icons
import InsuranceThickIcon from "@icons/myicons/contract/insurance.svg?react";
import HomeThickIcon from "@icons/myicons/contract/home-house.svg?react";
import FinanceIcon from "@icons/myicons/contract/finance.svg?react";
import SavingsIcon from "@icons/myicons/contract/savings.svg?react";
import TelecommuncationIcon from "@icons/myicons/contract/telecommunication.svg?react";
import LeisureIcon from "@icons/myicons/contract/leisure.svg?react";
import ChildrenIcon from "@icons/myicons/contract/children.svg?react";
import CarIcon from "@icons/myicons/contract/car.svg?react";
import ProductivityIcon from "@icons/myicons/contract/productivity.svg?react";
import SocialServicesIcon from "@icons/myicons/contract/social-services.svg?react";
import NotClassifiedIcon from "@icons/myicons/contract/not-classified.svg?react";
//#endregion
//#region Contracts thin category icons
import InsuranceThinIcon from "@icons/myicons/contract-thin/insurance-thin.svg?react";
import HomeThinIcon from "@icons/myicons/contract-thin/home-house-thin.svg?react";
import FinanceThinIcon from "@icons/myicons/contract-thin/finance-thin.svg?react";
import SavingsThinIcon from "@icons/myicons/contract-thin/savings-thin.svg?react";
import TelecommuncationThinIcon from "@icons/myicons/contract-thin/telecommunication-thin.svg?react";
import LeisureThinIcon from "@icons/myicons/contract-thin/leisure-thin.svg?react";
import ChildrenThinIcon from "@icons/myicons/contract-thin/children-thin.svg?react";
import CarThinIcon from "@icons/myicons/contract-thin/car-thin.svg?react";
import ProductivityThinIcon from "@icons/myicons/contract-thin/productivity-thin.svg?react";
import SocialServicesThinIcon from "@icons/myicons/contract-thin/social-services-thin.svg?react";
import NotClassifiedThinIcon from "@icons/myicons/contract-thin/not-classified-thin.svg?react";
//#endregion
//#region Contracts time interval icons
import TwelveMonthsIcon from "@icons/myicons/twelve-months.svg?react";
import ThreeMonthsIcon from "@icons/myicons/three-months.svg?react";
import SixMonthsIcon from "@icons/myicons/six-months.svg?react";
import OneMonthIcon from "@icons/myicons/one-month.svg?react";
import TwoMonthsIcon from "@icons/myicons/two-months.svg?react";
import OtherIcon from "@icons/myicons/other.svg?react";
import { BankAvatar } from "@rentablo/components";
import {
  ContractCategory,
  TimeInterval,
} from "@features/contractAnalyzer/types";
//#endregion

const COMMMON_ICON_SIZE = { width: "24px", height: "24px" };
const CONTRACTS_ICON_SIZE = { width: "20px", height: "20px" };
const CONTRACTS_SMALL_ICON_SIZE = { width: "16px", height: "16px" };

export const hasDefaultIcon = (url: string) =>
  url.includes("DEPOT_square") ||
  url.includes("CHECKING_square") ||
  url.includes("INSURANCE_square") ||
  url.includes("MATERIAL_ASSETS_square");

export const accountTypeIconMapper: {
  [key in AccountTypeEnum]: ReactElement<SvgIconProps>;
} = {
  "01_depot": <DepotIcon {...COMMMON_ICON_SIZE} />,
  "02_cash": <CashIcon {...COMMMON_ICON_SIZE} />,
  "04_insurance": <InsuranceIcon {...COMMMON_ICON_SIZE} />,
  "05_material_assets": <AssetsIcon {...COMMMON_ICON_SIZE} />,
};

export const contractCategoryIconMapper: {
  [key in ContractCategory]: ReactElement<SvgIconProps>;
} = {
  7: <InsuranceThickIcon {...CONTRACTS_ICON_SIZE} />,
  8: <HomeThickIcon {...CONTRACTS_ICON_SIZE} />,
  1: <FinanceIcon {...CONTRACTS_ICON_SIZE} />,
  6: <SavingsIcon {...CONTRACTS_ICON_SIZE} />,
  4: <TelecommuncationIcon {...CONTRACTS_ICON_SIZE} />,
  2: <LeisureIcon {...CONTRACTS_ICON_SIZE} />,
  3: <ChildrenIcon {...CONTRACTS_ICON_SIZE} />,
  5: <CarIcon {...CONTRACTS_ICON_SIZE} />,
  74: <SocialServicesIcon {...CONTRACTS_ICON_SIZE} />,
  75: <ProductivityIcon {...CONTRACTS_ICON_SIZE} />,
  notClassified: <NotClassifiedIcon {...CONTRACTS_ICON_SIZE} />,
};

export const contractCategorySmallIconMapper: {
  [key in ContractCategory]: ReactElement<SvgIconProps>;
} = {
  7: <InsuranceThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  8: <HomeThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  1: <FinanceThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  6: <SavingsThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  4: <TelecommuncationThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  2: <LeisureThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  3: <ChildrenThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  5: <CarThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  74: <SocialServicesThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  75: <ProductivityThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
  notClassified: <NotClassifiedThinIcon {...CONTRACTS_SMALL_ICON_SIZE} />,
};

export const cashflowAnalyzerIconMapper: Record<string, ReactElement> = {
  UNKNOWN: <NotClassifiedThinIcon {...CONTRACTS_ICON_SIZE} />,
  BANK_AND_CREDIT: <BankCreditIcon {...CONTRACTS_ICON_SIZE} />,
  Custom: <CustomIcon {...CONTRACTS_ICON_SIZE} />,
  INCOME: <FinanceIcon {...CONTRACTS_ICON_SIZE} />,
  LEISURE: <LeisureIcon {...CONTRACTS_ICON_SIZE} />,
  HEALTH_AND_WELLNESS: <HealthIcon {...CONTRACTS_ICON_SIZE} />,
  CHILDREN: <ChildrenIcon {...CONTRACTS_ICON_SIZE} />,
  COST_OF_LIVING: <LifestyleIcon {...CONTRACTS_ICON_SIZE} />,
  MOBILITY: <CarIcon {...CONTRACTS_ICON_SIZE} />,
  TRAVELLIING: <TravelIcon {...CONTRACTS_ICON_SIZE} />,
  SHOPPING_AND_ENTERTAINMENT: <ShoppingIcon {...CONTRACTS_ICON_SIZE} />,
  SAVING_AND_INVESTING: <SavingsIcon {...CONTRACTS_ICON_SIZE} />,
  INSURANCE: <InsuranceIcon {...CONTRACTS_ICON_SIZE} />,
  HOME_AND_LIVING: <HomeIcon {...CONTRACTS_ICON_SIZE} />,
};

export const cashflowAnalyzerSlugIconMapper: Record<string, ReactElement> = {
  UNKNOWN: <NotClassifiedThinIcon {...CONTRACTS_ICON_SIZE} />,
  BANK_AND_CREDIT: <BankCreditIcon {...CONTRACTS_ICON_SIZE} />,
  INCOME: <FinanceIcon {...CONTRACTS_ICON_SIZE} />,
  LEISURE: <LeisureIcon {...CONTRACTS_ICON_SIZE} />,
  HEALTH_AND_WELLNESS: <HealthIcon {...CONTRACTS_ICON_SIZE} />,
  CHILDREN: <ChildrenIcon {...CONTRACTS_ICON_SIZE} />,
  COST_OF_LIVING: <LifestyleIcon {...CONTRACTS_ICON_SIZE} />,
  MOBILITY: <CarIcon {...CONTRACTS_ICON_SIZE} />,
  SHOPPING_AND_ENTERTAINMENT: <ShoppingIcon {...CONTRACTS_ICON_SIZE} />,
  SAVING_AND_INVESTING: <SavingsIcon {...CONTRACTS_ICON_SIZE} />,
  INSURANCE: <InsuranceIcon {...CONTRACTS_ICON_SIZE} />,
  HOME_AND_LIVING: <HomeIcon {...CONTRACTS_ICON_SIZE} />,
};

export const timeIntervalIcons: {
  [key in TimeInterval]: ReactElement<SvgIconProps>;
} = {
  1: <OneMonthIcon {...COMMMON_ICON_SIZE} />,
  2: <TwoMonthsIcon {...COMMMON_ICON_SIZE} />,
  3: <ThreeMonthsIcon {...COMMMON_ICON_SIZE} />,
  6: <SixMonthsIcon {...COMMMON_ICON_SIZE} />,
  12: <TwelveMonthsIcon {...COMMMON_ICON_SIZE} />,
  other: <OtherIcon {...COMMMON_ICON_SIZE} />,
};

export const ReferenceIcon = () => <RefIcon {...COMMMON_ICON_SIZE} />;

export const getAccountIcon = (
  theme: Theme,
  size: number,
  account: Account,
  alwaysDisplayIcon: boolean = false
) => {
  if (!account) return <></>;
  const iconSizes = { width: `${size}px`, height: `${size}px` };

  if (account.depotIds?.length && !alwaysDisplayIcon) {
    return (
      <Box
        sx={{
          ...iconSizes,
          "& svg": iconSizes,
        }}
        color={theme.palette.tertiary.main}
      >
        <ReferenceIcon />
      </Box>
    );
  }

  if (!hasDefaultIcon(account.iconUrl)) {
    return <BankAvatar imgUrl={account.iconUrl} size={size} />;
  }

  return (
    <Box
      sx={{
        ...iconSizes,
        "& svg": iconSizes,
      }}
      color={theme.palette.tertiary.dark}
    >
      {accountTypeIconMapper[account.type as AccountTypeEnum]}
    </Box>
  );
};
